import { type CSSProperties } from "react";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { reorderIds } from "@shared/catalogue-order";
import { readLocalized } from "@shared/i18n";
import type { Money, OfferItem } from "@shared/types/roomOffer";
import LocalizedTextField from "../LocalizedTextField";
import AssetField from "../AssetField";
import { tokens } from "../../theme";
import OfferIconPicker from "./OfferIconPicker";

interface Props {
  items: OfferItem[];
  onChange: (next: OfferItem[]) => void;
  /** `false` for the amenity list — towels have a name and no price. */
  priced: boolean;
  /** Currency shown beside each price. Follows the offer's own. */
  currency: string;
  supportedLocales: string[];
  defaultLocale: string;
  /** Set when the server refused the list, so the card can say so. */
  error?: string;
}

/**
 * Mint an id here rather than let the store do it.
 *
 * The store WILL mint one for a row that arrives without it, but the row needs
 * a stable key the moment it exists: React lists it by id and dnd-kit sorts by
 * id, and a row keyed by its array index loses its focus and its half-typed
 * name the first time somebody drags it.
 */
function newItem(order: number): OfferItem {
  return {
    id: `it-${Math.random().toString(36).slice(2, 10)}`,
    label: "",
    icon: "generic",
    enabled: true,
    order,
  };
}

/**
 * The pick list an offer puts in front of a guest (`POS-5`).
 *
 * One editor for both shapes the doctrine names, differing only in whether a
 * price column exists: goods for an `order` (room service, minibar) and
 * amenities for a `booking · asap` (towels, refills — order-shaped with nothing
 * to charge). A `booking · slots` has no list here at all because its list is
 * GENERATED from the timetable, and a promotion has none because Back is the
 * only way out of one.
 *
 * **Order is what a guest reads first**, so it is dragged rather than sorted.
 * The affordance is the one the Apps and Radio grids already use — press and
 * drag the grip, or focus it and use Space/arrows — because an operator who has
 * arranged the app tiles has already learned this, and because the order things
 * appear in on a television must not be a mouse-only decision.
 *
 * The array order IS the order: `OfferItem.order` is re-stamped from the index
 * on every write server-side, so the two can never disagree.
 */
export default function OfferItemsEditor({
  items,
  onChange,
  priced,
  currency,
  supportedLocales,
  defaultLocale,
  error,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const ids = items.map((i) => i.id);

  const patch = (id: string, part: Partial<OfferItem>) =>
    onChange(items.map((it) => (it.id === id ? { ...it, ...part } : it)));

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const next = reorderIds(ids, String(active.id), String(over.id));
    if (!next) return;
    const byId = new Map(items.map((it) => [it.id, it]));
    onChange(
      next.flatMap((id, index) => {
        const item = byId.get(id);
        // Re-stamp `order` locally too. The server does it as well, but a list
        // that only agreed with itself after a round trip would draw the old
        // numbers into the preview in between.
        return item ? [{ ...item, order: index }] : [];
      })
    );
  };

  return (
    <Stack spacing={1.5}>
      {error && (
        <Typography sx={{ fontSize: 12.5, color: tokens.error }}>
          {error}
        </Typography>
      )}

      {items.length === 0 ? (
        <Typography sx={{ fontSize: 13.5, color: tokens.inkMuted }}>
          Nothing to pick from yet. Add a row for each thing a guest can choose
          — the guest sees them in this order.
        </Typography>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <Stack spacing={1}>
              {items.map((item, index) => (
                <SortableItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  priced={priced}
                  currency={currency}
                  supportedLocales={supportedLocales}
                  defaultLocale={defaultLocale}
                  onPatch={(part) => patch(item.id, part)}
                  onRemove={() =>
                    onChange(
                      items
                        .filter((it) => it.id !== item.id)
                        .map((it, n) => ({ ...it, order: n }))
                    )
                  }
                />
              ))}
            </Stack>
          </SortableContext>
        </DndContext>
      )}

      <Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onChange([...items, newItem(items.length)])}
          data-testid="offer-item-add"
        >
          Add row
        </Button>
      </Box>
    </Stack>
  );
}

interface RowProps {
  item: OfferItem;
  index: number;
  priced: boolean;
  currency: string;
  supportedLocales: string[];
  defaultLocale: string;
  onPatch: (part: Partial<OfferItem>) => void;
  onRemove: () => void;
}

function SortableItemRow(props: RowProps) {
  const sortable = useSortable({ id: props.item.id });
  return (
    <ItemRow
      {...props}
      setNodeRef={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
        opacity: sortable.isDragging ? 0.4 : undefined,
      }}
      grip={{ ...sortable.attributes, ...sortable.listeners }}
    />
  );
}

function ItemRow({
  item,
  index,
  priced,
  currency,
  supportedLocales,
  defaultLocale,
  onPatch,
  onRemove,
  setNodeRef,
  style,
  grip,
}: RowProps & {
  setNodeRef?: (el: HTMLElement | null) => void;
  style?: CSSProperties;
  grip?: Record<string, unknown>;
}) {
  const name = readLocalized(item.label, defaultLocale);
  const amount = item.price?.amount;

  return (
    <Box
      ref={setNodeRef}
      style={style}
      data-testid={`offer-item-${index}`}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1,
        p: 1.25,
        border: "1px solid",
        borderColor: tokens.divider,
        borderRadius: "12px",
        bgcolor: tokens.surface,
      }}
    >
      <Tooltip title="Drag to reorder">
        <IconButton
          size="small"
          aria-label={`Reorder ${name || `row ${index + 1}`}`}
          data-testid={`offer-item-grip-${index}`}
          {...grip}
          sx={{
            mt: 0.5,
            color: tokens.inkFaint,
            cursor: "grab",
            "&:active": { cursor: "grabbing" },
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Box sx={{ mt: 0.25 }}>
        <OfferIconPicker
          value={item.icon}
          onChange={(icon) => onPatch({ icon })}
          rowLabel={name}
          testId={`offer-item-icon-${index}`}
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 160 }}>
        <LocalizedTextField
          value={item.label}
          supportedLocales={supportedLocales}
          defaultLocale={defaultLocale}
          onChange={(label) => onPatch({ label })}
          placeholder="What the guest reads, e.g. Handdukar"
          testId={`offer-item-label-${index}`}
        />
        {/* Optional and deliberately last: `icon` is the default and
            photography is the exception, per docs/room-offers.md §2. */}
        <Box sx={{ mt: 1 }}>
          <AssetField
            label="Photo (optional)"
            value={item.imageUrl ?? ""}
            preview
            onChange={(v) => onPatch({ imageUrl: v || undefined })}
            testId={`offer-item-image-${index}`}
          />
        </Box>
      </Box>

      {priced && (
        <TextField
          size="small"
          label="Price"
          type="number"
          value={amount ?? ""}
          onChange={(e) =>
            onPatch({ price: parsePrice(e.target.value, currency) })
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{currency}</InputAdornment>
            ),
          }}
          inputProps={{
            min: 0,
            "data-testid": `offer-item-price-${index}`,
            "aria-label": `Price for ${name || `row ${index + 1}`}`,
          }}
          // Empty is FREE, not zero. A zero would put a 0 kr line into the
          // revenue series, which docs/room-offers.md §2 refuses by name.
          helperText="Empty = free"
          sx={{ width: 132, flex: "none" }}
        />
      )}

      <Tooltip title={item.enabled ? "Shown to guests" : "Hidden"}>
        <Switch
          size="small"
          checked={item.enabled}
          onChange={(e) => onPatch({ enabled: e.target.checked })}
          inputProps={{
            "aria-label": `Show ${name || `row ${index + 1}`} to guests`,
          }}
          sx={{ mt: 0.5, flex: "none" }}
        />
      </Tooltip>

      <Tooltip title="Remove row">
        <IconButton
          size="small"
          onClick={onRemove}
          aria-label={`Remove ${name || `row ${index + 1}`}`}
          data-testid={`offer-item-remove-${index}`}
          sx={{ mt: 0.5, color: tokens.inkFaint }}
        >
          <DeleteOutlineIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

/**
 * A typed price, or `undefined` for free.
 *
 * Blank must not become `{ amount: 0 }`: absent means "an amenity, nothing to
 * charge" and zero means "we charge nothing for it", and only the first of
 * those keeps a zero out of the revenue series.
 */
function parsePrice(raw: string, currency: string): Money | undefined {
  const text = raw.trim();
  if (text === "") return undefined;
  const amount = Number(text);
  if (!Number.isFinite(amount)) return undefined;
  return { amount, currency };
}
